/**
 * Client License Service
 *
 * Handles Stripe checkout for client licenses ($109/year).
 * Mentors pay for client licenses to manage their clients.
 *
 * Uses the Stripe Firebase Extension to create checkout sessions.
 * License validity period is 1 year from purchase date.
 */

import { collection, addDoc, doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/firebase';
import tiersConfig from '../config/tiers.json';

// Get client license price ID from tiers configuration
const getMentorClientTier = () => {
  return tiersConfig.tiers?.mentorClient || null;
};

/**
 * Get the appropriate price ID based on environment
 * Uses the yearly price ID from the mentorClient tier configuration
 * @returns {string} Price ID
 */
export const getClientLicensePriceId = () => {
  const mentorClientTier = getMentorClientTier();
  if (mentorClientTier && mentorClientTier.stripe && mentorClientTier.stripe.priceId) {
    return mentorClientTier.stripe.priceId.yearly;
  }
  // Fallback to a placeholder if tier config not found
  console.error('MentorClient tier not found in configuration');
  return null;
};

/**
 * Get the price for client licenses from tier configuration
 * @returns {number} Price in dollars
 */
export const getClientLicensePrice = () => {
  const mentorClientTier = getMentorClientTier();
  if (mentorClientTier && mentorClientTier.price) {
    return mentorClientTier.price.yearly || 109;
  }
  return 109; // $109/year fallback
};

/**
 * Get client license pricing details
 * @returns {Object} Pricing information
 */
export const getClientLicensePricing = () => {
  const mentorClientTier = getMentorClientTier();
  const price = mentorClientTier?.price?.yearly || 109;
  const currency = mentorClientTier?.price?.currency?.toUpperCase() || 'USD';

  return {
    amount: price,
    currency,
    interval: 'year',
    description: 'Client License - Annual',
    durationDays: 365,
  };
};

/**
 * Create a Stripe checkout session for a client license
 *
 * This uses the Stripe Firebase Extension's Firestore-based approach:
 * 1. Creates a document in the checkout_sessions subcollection
 * 2. The extension watches for this document and creates the Stripe checkout session
 * 3. We listen for the checkout session URL in Firestore
 * 4. Redirects the user to Stripe Checkout
 * 5. On successful payment, webhook triggers provisionClient Cloud Function
 *
 * @param {Object} options - Configuration options
 * @param {string} options.userId - Mentor's Firebase User ID (required)
 * @param {string} options.clientEmail - Client's email address (required)
 * @param {string} [options.successUrl] - URL to redirect to after successful payment
 * @param {string} [options.cancelUrl] - URL to redirect to if user cancels
 * @param {boolean} [options.redirect=true] - Whether to automatically redirect (default: true)
 * @param {string} [options.clientUserId] - Client's user ID (for renewals)
 * @param {boolean} [options.isRenewal=false] - Whether this is a license renewal
 * @returns {Promise<string>} Checkout session URL
 */
export const createClientLicenseCheckout = async (options) => {
  const {
    userId,
    clientEmail,
    successUrl,
    cancelUrl,
    redirect = true,
    clientUserId = null,
    isRenewal = false,
  } = options;

  if (!userId || typeof userId !== 'string') {
    throw new Error('User ID is required and must be a string');
  }

  if (!clientEmail || typeof clientEmail !== 'string') {
    throw new Error('Client email is required and must be a string');
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(clientEmail)) {
    throw new Error('Invalid email address format');
  }

  // Get price ID from configuration
  const priceId = getClientLicensePriceId();
  if (!priceId) {
    throw new Error('Client license price ID not configured. Please contact support.');
  }

  try {
    // Build URLs - defaults to mentor clients page
    const actionParam = isRenewal ? 'client_renewed' : 'client_added';
    const defaultSuccessUrl =
      successUrl ||
      `${window.location.origin}/mentor/clients?${actionParam}=true&email=${encodeURIComponent(clientEmail)}`;
    const defaultCancelUrl =
      cancelUrl ||
      `${window.location.origin}/mentor/clients?canceled=true`;

    // Create checkout session document in Firestore
    // The Stripe Firebase Extension watches this collection and creates the Stripe checkout session
    const checkoutSessionsRef = collection(
      db,
      'stripe_customers',
      userId,
      'checkout_sessions'
    );

    // Create the checkout session document
    // The extension expects: price (or priceId), success_url, cancel_url, mode
    // We add metadata to pass client info to the webhook
    const checkoutSessionDoc = await addDoc(checkoutSessionsRef, {
      price: priceId,
      success_url: defaultSuccessUrl,
      cancel_url: defaultCancelUrl,
      mode: 'payment', // One-time payment, not subscription
      metadata: {
        type: 'client_license',
        client_email: clientEmail,
        mentor_id: userId,
        client_user_id: clientUserId || '',
        is_renewal: isRenewal ? 'true' : 'false',
      },
    });

    // Wait for the extension to populate the checkout session URL
    // The extension will update the document with a 'url' field when ready
    return new Promise((resolve, reject) => {
      let unsubscribe = null;

      // Set up a timeout (30 seconds)
      const timeout = setTimeout(() => {
        if (unsubscribe) unsubscribe();
        reject(new Error('Checkout session creation timed out. Please try again.'));
      }, 30000);

      // Listen for the checkout session document to be updated with the URL
      const checkoutSessionDocRef = doc(checkoutSessionsRef, checkoutSessionDoc.id);
      unsubscribe = onSnapshot(
        checkoutSessionDocRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error('Checkout session document was deleted.'));
            return;
          }

          const data = snapshot.data();

          // Check for errors from the extension first
          if (data.error) {
            clearTimeout(timeout);
            if (unsubscribe) unsubscribe();
            reject(new Error(data.error.message || 'Failed to create checkout session'));
            return;
          }

          // Check if the extension has populated the URL
          if (data.url) {
            clearTimeout(timeout);
            if (unsubscribe) unsubscribe();

            const checkoutUrl = data.url;

            // Validate URL format
            try {
              new URL(checkoutUrl);
            } catch (urlError) {
              reject(new Error('Invalid URL format returned from Stripe checkout'));
              return;
            }

            // Redirect if requested (default behavior)
            if (redirect) {
              window.location.href = checkoutUrl;
              resolve(checkoutUrl);
            } else {
              resolve(checkoutUrl);
            }
          }
        },
        (error) => {
          clearTimeout(timeout);
          unsubscribe();
          reject(new Error(`Failed to listen for checkout session: ${error.message}`));
        }
      );
    });
  } catch (error) {
    // Handle Firestore errors
    if (error.code) {
      switch (error.code) {
        case 'permission-denied':
          throw new Error(
            'Permission denied. Please ensure you are logged in and have permission to create checkout sessions.'
          );
        case 'unauthenticated':
          throw new Error('You must be logged in to purchase a client license.');
        case 'invalid-argument':
          throw new Error('Invalid request. Please try again or contact support.');
        case 'deadline-exceeded':
          throw new Error('Request timed out. Please try again.');
        case 'resource-exhausted':
          throw new Error('Service temporarily unavailable. Please try again later.');
        default:
          if (error.message) {
            throw new Error(error.message);
          }
          throw new Error(
            `Failed to create checkout session: ${error.code || 'Unknown error'}`
          );
      }
    }

    // Handle generic errors
    if (error.message) {
      throw error;
    }

    // Fallback for unknown errors
    throw new Error(
      'An unexpected error occurred while creating the checkout session. Please try again or contact support.'
    );
  }
};

/**
 * Create a renewal checkout session for an existing client
 * @param {Object} options - Options
 * @param {string} options.userId - Mentor's user ID
 * @param {string} options.clientEmail - Client's email
 * @param {string} options.clientUserId - Client's user ID
 * @returns {Promise<string>} Checkout session URL
 */
export const createClientLicenseRenewal = async (options) => {
  return createClientLicenseCheckout({
    ...options,
    isRenewal: true,
  });
};

/**
 * Get mentor clients with their license status
 * @returns {Promise<Array>} Array of clients with license info
 */
export const getMentorClientsWithLicenseStatus = async () => {
  try {
    const getMentorClientsWithLicenseStatusFn = httpsCallable(
      functions,
      'getMentorClientsWithLicenseStatus'
    );
    const result = await getMentorClientsWithLicenseStatusFn();
    return result.data;
  } catch (error) {
    console.error('Error fetching clients with license status:', error);
    throw error;
  }
};

/**
 * Check license status for a specific client
 * @param {string} clientUserId - Client's user ID
 * @returns {Promise<Object>} License status
 */
export const checkClientLicenseStatus = async (clientUserId) => {
  try {
    const checkClientLicenseStatusFn = httpsCallable(
      functions,
      'checkClientLicenseStatus'
    );
    const result = await checkClientLicenseStatusFn({ clientUserId });
    return result.data;
  } catch (error) {
    console.error('Error checking client license status:', error);
    throw error;
  }
};

/**
 * Check if the current user has valid mentor access
 * @returns {Promise<Object>} Access status
 */
export const checkMentorAccess = async () => {
  try {
    const checkMentorAccessFn = httpsCallable(functions, 'checkMentorAccess');
    const result = await checkMentorAccessFn();
    return result.data;
  } catch (error) {
    console.error('Error checking mentor access:', error);
    throw error;
  }
};

/**
 * Get mentor-client credit status
 * This returns credit usage for a specific mentor-client relationship
 * (separate from personal subscription credits)
 * @param {string} clientUserId - Client's user ID
 * @param {string} mentorId - Mentor's user ID
 * @returns {Promise<Object>} Credit status { hasCredits, available, used, limit }
 */
export const getMentorClientCreditStatus = async (clientUserId, mentorId) => {
  try {
    const getMentorClientCreditStatusFn = httpsCallable(
      functions,
      'getMentorClientCreditStatus'
    );
    const result = await getMentorClientCreditStatusFn({ clientUserId, mentorId });
    return result.data;
  } catch (error) {
    console.error('Error getting mentor-client credit status:', error);
    throw error;
  }
};

/**
 * Format days remaining into a human-readable string
 * @param {number} daysRemaining - Number of days until expiration
 * @returns {string} Formatted string
 */
export const formatLicenseExpiration = (daysRemaining) => {
  if (daysRemaining === null || daysRemaining === undefined) {
    return 'No license';
  }

  if (daysRemaining <= 0) {
    return 'Expired';
  }

  if (daysRemaining === 1) {
    return 'Expires tomorrow';
  }

  if (daysRemaining <= 7) {
    return `Expires in ${daysRemaining} days`;
  }

  if (daysRemaining <= 30) {
    const weeks = Math.floor(daysRemaining / 7);
    return `Expires in ${weeks} week${weeks > 1 ? 's' : ''}`;
  }

  if (daysRemaining <= 365) {
    const months = Math.floor(daysRemaining / 30);
    return `Expires in ${months} month${months > 1 ? 's' : ''}`;
  }

  return `Expires in ${Math.floor(daysRemaining / 365)} year(s)`;
};

/**
 * Get license status badge color
 * @param {string} licenseStatus - License status (active, expired, no_license)
 * @param {number} daysRemaining - Days until expiration
 * @returns {string} CSS class name for the badge
 */
export const getLicenseStatusClass = (licenseStatus, daysRemaining) => {
  if (licenseStatus === 'expired' || licenseStatus === 'no_license') {
    return 'license-expired';
  }

  if (daysRemaining <= 30) {
    return 'license-expiring-soon';
  }

  return 'license-active';
};

export default {
  createClientLicenseCheckout,
  createClientLicenseRenewal,
  getClientLicensePrice,
  getClientLicensePriceId,
  getClientLicensePricing,
  getMentorClientsWithLicenseStatus,
  checkClientLicenseStatus,
  checkMentorAccess,
  getMentorClientCreditStatus,
  formatLicenseExpiration,
  getLicenseStatusClass,
};
